import { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  assignTransportLineEmployees,
  createTransportLine,
  deleteTransportLineAdjustment,
  getTransportLine,
  getTransportLineAdjustments,
  getTransportLines,
  unassignTransportLineEmployees,
  updateTransportLine,
  upsertTransportLineAdjustment,
} from "../../api/transportLines";
import "bootstrap/dist/css/bootstrap.rtl.min.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function cairoToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Cairo" });
}

function suggestLineCode(name) {
  const latin = name
    .trim()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return latin.length >= 2 ? latin : `L${Date.now().toString().slice(-4)}`;
}

function employeeLabel(emp) {
  const name = emp?.name || emp?.fullName || "بدون اسم";
  const code = emp?.code || emp?.employeeCode || "";
  return code ? `${name} (${code})` : name;
}

const EMPTY_LINE_FORM = {
  name: "",
  code: "",
  area: "",
  description: "",
  extraGraceMinutes: 0,
};

const EMPTY_DAY_FORM = {
  date: cairoToday(),
  extraGraceMinutes: 20,
  delayedStartTime: "",
  reason: "",
};

export default function TransportLines({ readOnly = false }) {
  const { employeesByBranch } = useData();
  const [lines, setLines] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_LINE_FORM);
  const [dayForm, setDayForm] = useState(EMPTY_DAY_FORM);
  const [assignIds, setAssignIds] = useState([]);

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e && e.id && e.status !== "terminated"),
    [employeesByBranch],
  );

  const loadLines = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getTransportLines({ includeInactive: true });
      setLines(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل خطوط المواصلات"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      setAdjustments([]);
      return;
    }
    try {
      const [line, days] = await Promise.all([
        getTransportLine(id),
        getTransportLineAdjustments(id),
      ]);
      setDetail(line);
      setAdjustments(Array.isArray(days) ? days : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل تفاصيل الخط"));
    }
  }, []);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const resetForm = () => {
    setForm(EMPTY_LINE_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (line) => {
    setForm({
      name: line.name || "",
      code: line.code || "",
      area: line.area || "",
      description: line.description || "",
      extraGraceMinutes: line.extraGraceMinutes ?? 0,
    });
    setEditingId(line.id);
    setShowForm(true);
  };

  const handleSubmitLine = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!form.name.trim() || !form.code.trim()) {
      setError("اسم الخط والكود مطلوبان");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateTransportLine(editingId, {
          name: form.name.trim(),
          area: form.area.trim() || undefined,
          description: form.description.trim() || undefined,
          extraGraceMinutes: Number(form.extraGraceMinutes) || 0,
        });
        setSuccess("تم تحديث الخط");
      } else {
        const created = await createTransportLine({
          name: form.name.trim(),
          code: form.code.trim().toUpperCase(),
          area: form.area.trim() || undefined,
          description: form.description.trim() || undefined,
          extraGraceMinutes: Number(form.extraGraceMinutes) || 0,
        });
        setSuccess("تم إنشاء الخط");
        setSelectedId(created?.id || null);
      }
      resetForm();
      await loadLines();
      if (selectedId) await loadDetail(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ الخط"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (line) => {
    setError("");
    try {
      await updateTransportLine(line.id, { isActive: !line.isActive });
      await loadLines();
      if (selectedId === line.id) await loadDetail(line.id);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تغيير حالة الخط"));
    }
  };

  const handleSaveDay = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const result = await upsertTransportLineAdjustment(selectedId, {
        date: dayForm.date,
        extraGraceMinutes: Number(dayForm.extraGraceMinutes) || 0,
        delayedStartTime: dayForm.delayedStartTime || null,
        reason: dayForm.reason.trim() || undefined,
      });
      setSuccess(
        result?.cleared
          ? "تم إلغاء سماح اليوم"
          : "تم حفظ سماح/ميعاد الخط لليوم",
      );
      setDayForm({ ...EMPTY_DAY_FORM, date: dayForm.date });
      await loadDetail(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ سماح اليوم"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDay = async (date) => {
    if (!selectedId) return;
    setError("");
    try {
      await deleteTransportLineAdjustment(selectedId, date.slice(0, 10));
      await loadDetail(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حذف السماح"));
    }
  };

  const assignedIds = new Set((detail?.employees || []).map((e) => e.id));
  const availableEmployees = allEmployees.filter((e) => !assignedIds.has(e.id));

  const handleAssign = async () => {
    if (!selectedId || assignIds.length === 0) return;
    setError("");
    setSaving(true);
    try {
      await assignTransportLineEmployees(selectedId, assignIds);
      setAssignIds([]);
      await loadLines();
      await loadDetail(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إضافة الموظفين للخط"));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (employeeId) => {
    if (!selectedId) return;
    setError("");
    try {
      await unassignTransportLineEmployees(selectedId, [employeeId]);
      await loadLines();
      await loadDetail(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إزالة الموظف من الخط"));
    }
  };

  return (
    <div className="container-fluid py-3" dir="rtl">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">خطوط المواصلات</h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
            الخط هو أتوبيس بيجمع موظفين من منطقة معينة، مستقل عن الفرع والإدارة.
            لو الأتوبيس اتأخر تقدر تدي سماح أو تأخّر ميعاد الحضور لكل موظفي
            الخط مرة واحدة.
          </p>
        </div>
        {!readOnly && !showForm && (
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + إضافة خط
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          />
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          />
        </div>
      )}

      {showForm && !readOnly && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>{editingId ? "تعديل الخط" : "خط مواصلات جديد"}</strong>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={resetForm}
            >
              إلغاء
            </button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmitLine}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">اسم الخط</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        name,
                        code:
                          editingId || prev.code
                            ? prev.code
                            : suggestLineCode(name),
                      }));
                    }}
                    placeholder="مثال: خط فيصل"
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">الكود</label>
                  <input
                    className="form-control"
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    disabled={Boolean(editingId)}
                    placeholder="FAISAL"
                    required
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">المنطقة</label>
                  <input
                    className="form-control"
                    value={form.area}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, area: e.target.value }))
                    }
                    placeholder="فيصل / الهرم"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">سماح ثابت (دقيقة)</label>
                  <input
                    type="number"
                    min="0"
                    max="180"
                    className="form-control"
                    value={form.extraGraceMinutes}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        extraGraceMinutes: e.target.value,
                      }))
                    }
                  />
                  <div className="form-text">
                    يضاف على سماح اللائحة لكل يوم
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">وصف</label>
                  <input
                    className="form-control"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="اختياري"
                  />
                </div>
              </div>
              <div className="mt-3">
                <button className="btn btn-primary" disabled={saving}>
                  {saving ? "جاري الحفظ..." : "حفظ الخط"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-5">
          <div className="card">
            <div className="card-header">الخطوط</div>
            <div className="card-body p-0">
              {loading ? (
                <p className="p-3 mb-0 text-muted">جاري التحميل...</p>
              ) : lines.length === 0 ? (
                <p className="p-3 mb-0 text-muted">لا توجد خطوط بعد</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>الخط</th>
                        <th>المنطقة</th>
                        <th>سماح</th>
                        <th>موظفون</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr
                          key={line.id}
                          className={selectedId === line.id ? "table-primary" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedId(line.id)}
                        >
                          <td>
                            <strong>{line.name}</strong>
                            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                              {line.code}
                              {!line.isActive ? " — متوقف" : ""}
                            </div>
                          </td>
                          <td>{line.area || "—"}</td>
                          <td>+{line.extraGraceMinutes || 0} د</td>
                          <td>{line.employeeCount ?? 0}</td>
                          <td>
                            {!readOnly && (
                              <div className="d-flex gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(line);
                                  }}
                                >
                                  تعديل
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleActive(line);
                                  }}
                                >
                                  {line.isActive ? "إيقاف" : "تفعيل"}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          {!selectedId || !detail ? (
            <div className="card">
              <div className="card-body text-muted">
                اختر خطاً لعرض الموظفين وتعديل سماح اليوم.
              </div>
            </div>
          ) : (
            <>
              <div className="card mb-3">
                <div className="card-header">
                  سماح ومواعيد {detail.name}
                  {detail.area ? ` — ${detail.area}` : ""}
                </div>
                <div className="card-body">
                  <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                    السماح الثابت للخط:{" "}
                    <strong>+{detail.extraGraceMinutes || 0} دقيقة</strong> فوق
                    سماح اللائحة. لو الأتوبيس اتأخر النهاردة، زوّد سماح اليوم
                    أو أخّر ميعاد الحضور لكل موظفي الخط.
                  </p>
                  {!readOnly && (
                    <form onSubmit={handleSaveDay}>
                      <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                          <label className="form-label">اليوم</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dayForm.date}
                            onChange={(e) =>
                              setDayForm((prev) => ({
                                ...prev,
                                date: e.target.value,
                              }))
                            }
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">سماح إضافي (دقيقة)</label>
                          <input
                            type="number"
                            min="0"
                            max="180"
                            className="form-control"
                            value={dayForm.extraGraceMinutes}
                            onChange={(e) =>
                              setDayForm((prev) => ({
                                ...prev,
                                extraGraceMinutes: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">ميعاد حضور متأخر</label>
                          <input
                            type="time"
                            className="form-control"
                            value={dayForm.delayedStartTime}
                            onChange={(e) =>
                              setDayForm((prev) => ({
                                ...prev,
                                delayedStartTime: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <button className="btn btn-warning w-100" disabled={saving}>
                            تطبيق على الخط
                          </button>
                        </div>
                        <div className="col-12">
                          <input
                            className="form-control"
                            value={dayForm.reason}
                            onChange={(e) =>
                              setDayForm((prev) => ({
                                ...prev,
                                reason: e.target.value,
                              }))
                            }
                            placeholder="السبب — مثال: زحام الطريق / عطل الأتوبيس"
                          />
                        </div>
                      </div>
                    </form>
                  )}

                  {adjustments.length > 0 && (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>اليوم</th>
                            <th>سماح إضافي</th>
                            <th>ميعاد متأخر</th>
                            <th>السبب</th>
                            {!readOnly && <th></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {adjustments.map((row) => {
                            const date = String(row.date).slice(0, 10);
                            return (
                              <tr key={row.id || date}>
                                <td>{date}</td>
                                <td>+{row.extraGraceMinutes || 0} د</td>
                                <td>{row.delayedStartTime || "—"}</td>
                                <td>{row.reason || "—"}</td>
                                {!readOnly && (
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteDay(date)}
                                    >
                                      حذف
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  موظفو الخط ({detail.employees?.length || 0})
                </div>
                <div className="card-body">
                  {!readOnly && (
                    <div className="row g-2 mb-3">
                      <div className="col-md-9">
                        <select
                          className="form-select"
                          value={assignIds[0] || ""}
                          onChange={(e) =>
                            setAssignIds(e.target.value ? [e.target.value] : [])
                          }
                        >
                          <option value="">اختر موظفاً لإضافته للخط</option>
                          {availableEmployees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {employeeLabel(emp)}
                              {emp.departmentName ? ` — ${emp.departmentName}` : ""}
                              {emp.branchName ? ` / ${emp.branchName}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary w-100"
                          disabled={saving || assignIds.length === 0}
                          onClick={handleAssign}
                        >
                          إضافة للخط
                        </button>
                      </div>
                    </div>
                  )}
                  {(detail.employees || []).length === 0 ? (
                    <p className="text-muted mb-0">لا يوجد موظفون على هذا الخط</p>
                  ) : (
                    <ul className="list-group">
                      {detail.employees.map((emp) => (
                        <li
                          key={emp.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <strong>{emp.fullName}</strong>{" "}
                            <span className="text-muted">{emp.employeeCode}</span>
                            <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                              {emp.jobTitle}
                              {emp.department?.name ? ` — ${emp.department.name}` : ""}
                              {emp.branch?.name ? ` / ${emp.branch.name}` : ""}
                            </div>
                          </div>
                          {!readOnly && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleUnassign(emp.id)}
                            >
                              إزالة
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
